import { useRef, useCallback, FC, useEffect } from "react";
import { useContext } from "react";
import { Animated, TouchableWithoutFeedback, Easing, View } from "react-native";

import { tailwind } from "src/foundations/styles";
import { Icon } from "src/foundations/ui";
import AccordionContext from "src/ui/components/Accordion/AccordionContext";

import { ANIMATION_OPEN_CLOSE_DURATION } from "./constants";

export type AccordionSummaryProps = {
  // TODO: add support for id
  // id?: string;
  // TODO: maybe we want to enable users to specify their own icon if we wish
  // expandIcon?: ReactNode
  verticalPadding?: "md" | "sm";
};

export const AccordionSummary: FC<AccordionSummaryProps> = ({
  // TODO: use the id as part of aria
  // id,
  children,
  verticalPadding = "md",
}) => {
  const accordionContextState = useContext(AccordionContext);
  const rotateValueHolderRef = useRef(new Animated.Value(0));

  const rotateToExpandedFunction = useCallback(() => {
    rotateValueHolderRef.current.setValue(0);
    Animated.timing(rotateValueHolderRef.current, {
      toValue: 1,
      duration: ANIMATION_OPEN_CLOSE_DURATION,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start();
  }, []);

  const rotateToCollapsedFunction = useCallback(() => {
    rotateValueHolderRef.current.setValue(0);
    Animated.timing(rotateValueHolderRef.current, {
      toValue: 1,
      duration: ANIMATION_OPEN_CLOSE_DURATION,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start();
  }, []);

  const rotateToExpandedData = rotateValueHolderRef.current.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"],
  });

  const rotateToCollapsedData = rotateValueHolderRef.current.interpolate({
    inputRange: [0, 1],
    outputRange: ["180deg", "0deg"],
  });

  useEffect(() => {
    if (accordionContextState.expanded) {
      rotateToExpandedFunction();
    } else {
      rotateToCollapsedFunction();
    }
  }, [accordionContextState.expanded, rotateToCollapsedFunction, rotateToExpandedFunction]);

  const paddingClass = verticalPadding === "md" ? "py-4" : "py-2";

  return (
    <TouchableWithoutFeedback
      onPress={accordionContextState.toggle}
      // TODO: need a id field for label by
      accessible={true}
      accessibilityRole="button"
      accessibilityLabel={
        accordionContextState.expanded ? "Closes the accordion" : "Opens the accordion"
      }
      accessibilityHint={
        accordionContextState.expanded
          ? "Hides accordion details from screen"
          : "Reveals accordion details on screen"
      }
      accessibilityState={{ expanded: accordionContextState.expanded }}
    >
      <View
        style={tailwind(`${paddingClass} flex flex-row flex-nowrap items-center justify-between`)}
      >
        <View>{children}</View>

        <Animated.View
          style={{
            transform: [
              {
                rotate: accordionContextState.expanded
                  ? rotateToExpandedData
                  : rotateToCollapsedData,
              },
            ],
          }}
        >
          <Icon size={12} name="chevron-down" />
        </Animated.View>
      </View>
    </TouchableWithoutFeedback>
  );
};

export default AccordionSummary;
